import React, { useState } from "react";
import {
  Box,
  Button,
  HStack,
  Icon,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useColorModeValue,
  VStack,
} from "@chakra-ui/react";
import { MdCheck } from "react-icons/md";
import { useAuth } from "../../../../auth-context/auth.context";

interface ManagedFirmSelectorModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const ManagedFirmSelectorModal: React.FC<ManagedFirmSelectorModalProps> = ({
                                                                             isOpen,
                                                                             onClose,
                                                                           }) => {
  const { user, updateSelectedFirm } = useAuth(); // Access `useAuth` context
  const [selectedFirmId, setSelectedFirmId] = useState<string>(
    user?.selectedFirm?.firmId?.toString() || ""
  );

  const handleSelectFirm = () => {
    const newSelectedFirm =
      sortedFirms.find(
        (firm: any) => firm?.firmId?.toString() === selectedFirmId
      )?.firm || null;

    updateSelectedFirm(newSelectedFirm); // Use updateSelectedFirm to update the selected firm
    onClose();
  };

  const bgColor = useColorModeValue("white", "navy.800");
  const borderColor = useColorModeValue(`#E6ECFA`, `rgba(135, 140, 189, 0.3)`);
  const selectedBgColor = useColorModeValue("blue.50", "blue.900"); // Highlight color for selected item
  const bgHover = useColorModeValue(
    {bg: "gray.100"},
    {bg: "whiteAlpha.50"}
  );

  const defaultFirm = user?.firm;
  const managedFirmsExcludingDefault = user?.managedFirms?.filter(
    (managedFirm: any) => managedFirm.firmId !== defaultFirm?.firmId
  );

  const sortedFirms = [
    ...(defaultFirm
      ? [{ firmId: defaultFirm.firmId, firm: defaultFirm }]
      : []),
    ...(managedFirmsExcludingDefault || []),
  ];

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered closeOnOverlayClick={false}>
      <ModalOverlay/>
      <ModalContent
        bg={bgColor}
        borderRadius="lg"
        borderColor={borderColor}
        borderWidth="1px"
      >
        <ModalHeader>Select a firm location</ModalHeader>
        {user?.selectedFirm && <ModalCloseButton/>}
        <ModalBody>
          <Box maxH="300px" overflowY="auto">
            {sortedFirms?.length > 0 ? (
              <VStack spacing="4" width="100%" align="stretch">
                {sortedFirms.map((firm: any) => (
                  <HStack
                    key={firm?.firmId}
                    justifyContent="space-between"
                    px={4}
                    py={3}
                    borderWidth="1px"
                    borderColor={
                      firm?.firmId?.toString() === selectedFirmId
                        ? "blue.500"
                        : borderColor
                    }
                    borderRadius="lg"
                    bg={
                      firm?.firmId?.toString() === selectedFirmId
                        ? selectedBgColor
                        : "transparent"
                    }
                    cursor="pointer"
                    onClick={() => setSelectedFirmId(firm?.firmId?.toString())} // Update the selected firmId
                    _hover={bgHover}
                  >
                    {/* Firm Name */}
                    <Text
                      fontSize="md"
                      fontWeight={
                        firm?.firmId === defaultFirm?.firmId ? "bold" : "normal"
                      }
                      flex="1"
                    >
                      {firm?.firm?.name}
                      {firm?.firmId === defaultFirm?.firmId ? " (Main)" : ""}
                    </Text>

                    {/* Check Icon for Selected Firm */}
                    {firm?.firmId?.toString() === selectedFirmId && (
                      <Icon as={MdCheck} color="blue.500" boxSize={6}/>
                    )}
                  </HStack>
                ))}
              </VStack>
            ) : (
              <Text fontSize="sm" color="gray.500">
                No managed firms available.
              </Text>
            )}
          </Box>
        </ModalBody>
        <ModalFooter>
          <Button
            colorScheme="brand"
            variant="ghost"
            mr={3}
            isDisabled={!user?.selectedFirm}
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            colorScheme="brand"
            onClick={handleSelectFirm}
            isDisabled={!selectedFirmId}
          >
            Select
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default ManagedFirmSelectorModal;